import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useSelector } from "react-redux";
import { KTable } from "@kyburz/kdesign-system";
import { getVehiclesNew as getVehicles } from "../../apiCalls/admin/vehicles";
import { RootStateInterface } from "../../interfaces/ReduxInterface";
import useSetState from "../../hooks/useSetState";
import { getVehicleFields } from "./vehicles/VehicleTableFields";
import { GroupData } from "../../interfaces/GroupInterface";

const MemoizedTable = React.memo(
  (props: any) => (
    <KTable
      columns={props.columns}
      data={props.data}
      hasFilter
      onSelectRows={props.onSelectRows}
      initialSelectedRows={props.initialSelectedRows}
      tableHeight={props.height}
    />
  ),
  (oldProps, newProps) => {
    return (
      oldProps.columns.length === newProps.columns.length &&
      oldProps.data === newProps.data &&
      JSON.stringify(oldProps.initialSelectedRows) ===
        JSON.stringify(newProps.initialSelectedRows) &&
      oldProps.reset === newProps.reset
    );
  },
);

const useStyles = makeStyles(() => ({
  root: {
    height: "calc(100vh - 465px)",
  },
}));

interface Props {
  groupData: GroupData;
  activeFilter: "inkl" | "notInkl" | "all";
  handleReset: () => void;
  reset: boolean;
  handleCheckedIds: (name: string, ids: string[]) => void;
  updateVehiclesLength: (length: number) => void;
}

const GroupViewVehiclesTable = ({
  groupData,
  activeFilter,
  handleReset,
  reset,
  handleCheckedIds,
  updateVehiclesLength,
}: Props) => {
  const classes = useStyles();
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );

  const [
    { items, filteredItems, preSelected, vehicleIds, loading },
    setState,
  ] = useSetState({
    items: [],
    filteredItems: [],
    preSelected: {},
    vehicleIds: [],
    loading: false,
  });

  const setSelectedRows = (
    isAllSelected: boolean,
    selectedRows: { key: number; value: boolean },
    latestSelected: number | string,
  ): void => {
    if (!reset) {
      const latestSelectedId = filteredItems[latestSelected]?.id;
      if (latestSelected === "all") {
        const ids = Object.keys(selectedRows).map((x) => filteredItems[x]?.id);
        const mergedWithoutDuplicates = [...new Set([...vehicleIds, ...ids])];
        if (mergedWithoutDuplicates?.length > vehicleIds?.length) {
          setState({ vehicleIds: mergedWithoutDuplicates });
        } else {
          setState({ vehicleIds: ids });
        }
      } else if (vehicleIds.includes(latestSelectedId)) {
        const removed = vehicleIds.filter((x) => x !== latestSelectedId);
        setState({ vehicleIds: removed });
      } else {
        const merged = vehicleIds.concat(latestSelectedId);
        setState({ vehicleIds: merged });
      }
    } else {
      handleReset();
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        defaultCanSort: false,
      },
      {
        Header: "Vehicle Type",
        accessor: "vehicleType",
        defaultCanSort: false,
      },
      {
        Header: "Brand",
        accessor: "brand",
        defaultCanSort: false,
      },
      {
        Header: "Imei",
        accessor: "imei1",
        defaultCanSort: false,
      },
    ],
    [],
  );

  useEffect(() => {
    if (filteredItems?.length && vehicleIds) {
      const reducedItems: { index: boolean } = filteredItems
        .map((x, index: boolean) => (vehicleIds.includes(x?.id) ? index : ""))
        .filter(String)
        .reduce((obj, item: number) => ({ ...obj, [item]: true }), {});
      setState({ preSelected: reducedItems });
    }
  }, [filteredItems, vehicleIds, activeFilter]);

  useEffect(() => {
    if (items?.length && activeFilter) {
      if (activeFilter === "inkl") {
        setState({
          filteredItems: items.filter((x) =>
            groupData?.vehicles?.includes(x.id),
          ),
        });
      } else if (activeFilter === "notInkl") {
        setState({
          filteredItems: items.filter(
            (x) => !groupData?.vehicles?.includes(x.id),
          ),
        });
      } else {
        setState({ filteredItems: items });
      }
    }
  }, [activeFilter, items]);

  useEffect(() => {
    if (groupData?.vehicles?.length) {
      setState({ vehicleIds: groupData?.vehicles });
    }
  }, [groupData?.vehicles]);

  useEffect(() => {
    handleCheckedIds("vehicleIds", vehicleIds);
  }, [vehicleIds]);

  useEffect(() => {
    setState({ loading: true });
    getVehicles(currentCustomer?.shortname, "", 1, 10000)
      .then((res) => {
        setState({
          items: res.data?.data?.map((x) => getVehicleFields(x)),
          loading: false,
        });
      })
      .catch((e) => {
        console.error(e);
        setState({ loading: false });
      });
  }, []);

  useEffect(() => {
    if (items.length) {
      updateVehiclesLength(items.length);
    }
  }, [items]);

  return (
    <div className={classes.root}>
      {loading ? (
        <>
          <Skeleton
            variant="rect"
            width="100%"
            height={100}
            style={{ marginTop: 20 }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height="calc(100vh - 545px)"
            style={{ marginTop: 8 }}
          />
        </>
      ) : (
        <MemoizedTable
          columns={columns}
          data={filteredItems}
          hasFilter
          onSelectRows={setSelectedRows}
          reset={reset}
          initialSelectedRows={preSelected}
          height="100%"
        />
      )}
    </div>
  );
};

export default GroupViewVehiclesTable;
